import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/format";

import {
  PageHeader,
  Loader,
  KpiCard,
  EmptyState,
} from "@/components/common/Bits";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Boxes,
  AlertTriangle,
  XCircle,
  IndianRupee,
  Plus,
  Pencil,
  Trash2,
  CalendarDays,
  RotateCcw,
  AlertCircle,
} from "lucide-react";

import { toast } from "sonner";

const TXN = ["purchase", "consumption", "adjustment", "wastage", "return"];

const PAGE_SIZE = 10;

const getToday = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();

  return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const formatDate = (date) => {
  if (!date) return "—";

  const d = new Date(`${date}T00:00:00`);

  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleDateString("en-IN");
};

const formatNumber = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) return "0";

  return num.toFixed(3).replace(/\.?0+$/, "");
};

const getTransactionPurchasePrice = (tx) => {
  if (!tx || tx.type !== "purchase") return null;

  const value =
    tx.purchase_price ?? tx.unit_price ?? tx.price ?? tx.purchasePrice;

  const num = Number(value);

  return Number.isFinite(num) ? num : null;
};

const getTransactionSupplier = (tx) => {
  if (!tx || tx.type !== "purchase") return "—";

  return tx.supplier ?? tx.supplier_name ?? tx.vendor ?? "—";
};

export default function Inventory() {
  const { branchId, branches } = useApp();

  const activeBranch = branchId === "all" ? branches?.[0]?.id : branchId;

  const [items, setItems] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const [dlg, setDlg] = useState(null);
  const [itemDlg, setItemDlg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [selectedDate, setSelectedDate] = useState(getToday);

  const [stockPage, setStockPage] = useState(1);
  const [activityPage, setActivityPage] = useState(1);

  const load = async () => {
    if (!activeBranch) {
      setItems([]);
      setTransactions([]);
      return;
    }

    try {
      const requests = [
        api
          .inventory(activeBranch)
          .then((r) => {
            setItems(Array.isArray(r.data) ? r.data : []);
          })
          .catch(() => {
            setItems([]);
          }),
      ];

      if (api.inventoryTransactions) {
        requests.push(
          api
            .inventoryTransactions(
              activeBranch,
              selectedDate === "all" ? undefined : selectedDate,
            )
            .then((r) => {
              setTransactions(Array.isArray(r.data) ? r.data : []);
            })
            .catch(() => {
              setTransactions([]);
            }),
        );
      } else {
        setTransactions([]);
      }

      await Promise.all(requests);
    } catch {
      setItems([]);
      setTransactions([]);
    }
  };

  useEffect(() => {
    setItems(null);

    setStockPage(1);
    setActivityPage(1);

    load();
  }, [activeBranch, selectedDate]);

  const requestDelete = (item) => {
    setConfirmDelete(item);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;

    const item = confirmDelete;

    setConfirmDelete(null);

    try {
      await api.deleteInventory(item.id);

      toast.success("Inventory item deleted");

      setStockPage(1);

      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete item");
    }
  };

  const isSameDate = (value, date) => {
    if (!value || !date) return false;

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) {
      return false;
    }

    const offset = d.getTimezoneOffset();

    const localDate = new Date(d.getTime() - offset * 60000)
      .toISOString()
      .slice(0, 10);

    return localDate === date;
  };

  const filteredItems = useMemo(() => {
    return Array.isArray(items) ? items : [];
  }, [items]);

  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) {
      return [];
    }

    if (selectedDate === "all") {
      return transactions;
    }

    return transactions.filter((tx) => {
      const date = tx.transaction_date || tx.created_at;

      return isSameDate(date, selectedDate);
    });
  }, [transactions, selectedDate]);

  const stockTotalPages = Math.max(
    1,
    Math.ceil(filteredItems.length / PAGE_SIZE),
  );

  const activityTotalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / PAGE_SIZE),
  );

  const paginatedItems = useMemo(() => {
    const start = (stockPage - 1) * PAGE_SIZE;

    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, stockPage]);

  const paginatedTransactions = useMemo(() => {
    const start = (activityPage - 1) * PAGE_SIZE;

    return filteredTransactions.slice(start, start + PAGE_SIZE);
  }, [filteredTransactions, activityPage]);

  useEffect(() => {
    if (stockPage > stockTotalPages) {
      setStockPage(stockTotalPages);
    }
  }, [stockPage, stockTotalPages]);

  useEffect(() => {
    if (activityPage > activityTotalPages) {
      setActivityPage(activityTotalPages);
    }
  }, [activityPage, activityTotalPages]);

  const getStockValue = (item) => {
    /*
     * Backend ka calculated stock_value
     * available ho to wahi use karo.
     *
     * Fallback:
     * current_stock × purchase_price
     */
    if (item.stock_value !== undefined && item.stock_value !== null) {
      return Number(item.stock_value) || 0;
    }

    return (
      (Number(item.current_stock) || 0) * (Number(item.purchase_price) || 0)
    );
  };

  const stats = useMemo(() => {
    const arr = filteredItems;

    return {
      total: arr.length,

      low: arr.filter(
        (i) =>
          Number(i.current_stock) <= Number(i.min_stock) &&
          Number(i.current_stock) > 0,
      ).length,

      out: arr.filter((i) => Number(i.current_stock) <= 0).length,

      value: arr.reduce((sum, item) => sum + getStockValue(item), 0),
    };
  }, [filteredItems]);

  if (!items) {
    return (
      <div className="p-6">
        <Loader label="Loading inventory" />
      </div>
    );
  }

  const statusOf = (item) => {
    const current = Number(item.current_stock) || 0;

    const min = Number(item.min_stock) || 0;

    if (current <= 0) {
      return "out";
    }

    if (current <= min) {
      return "low";
    }

    return "ok";
  };

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader
        title="Inventory"
        subtitle="Stock levels and consumption"
        actions={
          <Button
            onClick={() => setItemDlg("add")}
            className="rounded-xl font-semibold"
          >
            <Plus size={16} className="mr-1" />
            Add Item
          </Button>
        }
      />

      {/* ================= KPI CARDS ================= */}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Total Items"
          value={stats.total}
          icon={Boxes}
          accent="orange"
        />

        <KpiCard
          label="Low Stock"
          value={stats.low}
          icon={AlertTriangle}
          accent="amber"
        />

        <KpiCard
          label="Out of Stock"
          value={stats.out}
          icon={XCircle}
          accent="orange"
        />

        <KpiCard
          label="Stock Value"
          value={money(stats.value)}
          icon={IndianRupee}
          accent="emerald"
        />
      </div>

      {/* ================= DATE FILTER ================= */}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {selectedDate === "all"
              ? "Showing all inventory"
              : `Showing inventory for ${formatDate(selectedDate)}`}
          </p>

          <p className="text-xs text-muted-foreground">
            Use the date filter to review inventory activity by day.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <div className="relative">
            <CalendarDays
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />

            <Input
              type="date"
              value={selectedDate === "all" ? "" : selectedDate}
              onChange={(e) => setSelectedDate(e.target.value || "all")}
              className="h-10 w-[170px] rounded-xl pl-9"
              aria-label="Filter inventory by date"
            />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => setSelectedDate(getToday())}
            className="h-10 rounded-xl"
            title="Reset to today"
          >
            <RotateCcw size={15} className="mr-1.5" />
            Today
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => setSelectedDate("all")}
            className={cn(
              "h-10 rounded-xl",
              selectedDate === "all" && "bg-secondary",
            )}
          >
            All
          </Button>
        </div>
      </div>

      {/* ================= INVENTORY ACTIVITY ================= */}

      <div className="mb-6 rounded-2xl bg-card border border-border overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h3 className="font-semibold">Inventory Activity</h3>

            <p className="text-xs text-muted-foreground">
              Purchases, consumption, adjustments, wastage and returns
            </p>
          </div>

          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold">
            {selectedDate === "all" ? "All Dates" : formatDate(selectedDate)}
          </span>
        </div>

        {filteredTransactions.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No inventory activity for the selected date.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr className="text-center">
                    {[
                      "Item",
                      "Type",
                      "Quantity",
                      "Price / Unit",
                      "Supplier",
                      "Date",
                      "Note",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedTransactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="border-t border-border hover:bg-secondary/30"
                    >
                      <td className="px-4 py-3 text-center font-semibold">
                        {tx.item_name || "—"}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold capitalize">
                          {tx.type}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-center font-bold">
                        {formatNumber(tx.quantity)} {tx.unit || ""}
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        {getTransactionPurchasePrice(tx) !== null
                          ? money(getTransactionPurchasePrice(tx))
                          : "—"}
                      </td>

                      <td className="px-4 py-3 text-center text-muted-foreground whitespace-nowrap">
                        {getTransactionSupplier(tx)}
                      </td>

                      <td className="px-4 py-3 text-center text-muted-foreground whitespace-nowrap">
                        {tx.transaction_date
                          ? formatDate(tx.transaction_date)
                          : tx.created_at
                            ? new Date(tx.created_at).toLocaleDateString(
                                "en-IN",
                              )
                            : "—"}
                      </td>

                      <td className="px-4 py-3 text-center text-muted-foreground">
                        {tx.note || tx.reason || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredTransactions.length > PAGE_SIZE && (
              <Pagination
                page={activityPage}
                totalPages={activityTotalPages}
                total={filteredTransactions.length}
                pageSize={PAGE_SIZE}
                onPageChange={setActivityPage}
              />
            )}
          </>
        )}
      </div>

      {/* ================= CURRENT STOCK ================= */}

      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Current Stock</h3>

          <p className="text-xs text-muted-foreground">
            Live stock levels; the date filter above applies to
            activity/receipts.
          </p>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div
          className="rounded-2xl bg-card border border-border min-h-[360px] flex items-center justify-center"
          data-testid="inventory-empty-state"
        >
          <EmptyState
            icon={Boxes}
            title="No inventory items yet"
            subtitle="Add your first inventory item to start tracking stock levels and consumption."
          />
        </div>
      ) : (
        <>
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr className="text-center">
                    {[
                      "Item",
                      "Current",
                      "Min",
                      "Purchase Price",
                      "Supplier",
                      "Status",
                      "Action",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedItems.map((item) => {
                    const status = statusOf(item);

                    return (
                      <tr
                        key={item.id}
                        className="border-t border-border hover:bg-secondary/30"
                        data-testid={`inv-row-${item.id}`}
                      >
                        <td className="px-4 py-3 text-center font-semibold">
                          {item.name}
                        </td>

                        <td className="px-4 py-3 text-center font-bold">
                          {formatNumber(item.current_stock)}{" "}
                          <span className="text-muted-foreground font-normal">
                            {item.unit}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-center text-muted-foreground">
                          {formatNumber(item.min_stock)} {item.unit}
                        </td>

                        <td className="px-4 py-3 text-center">
                          {money(item.purchase_price || 0)}
                        </td>

                        <td className="px-4 py-3 text-center text-muted-foreground">
                          {item.supplier || "—"}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <span
                            className={cn(
                              "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                              status === "ok"
                                ? "bg-emerald-100 text-emerald-700"
                                : status === "low"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700",
                            )}
                          >
                            {status === "ok"
                              ? "In Stock"
                              : status === "low"
                                ? "Low"
                                : "Out"}
                          </span>
                        </td>

                        {/* ACTION */}

                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 rounded-lg p-0"
                              onClick={() => setDlg(item)}
                              title="Update stock"
                              data-testid={`inv-txn-${item.id}`}
                            >
                              <Boxes size={14} />
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 rounded-lg p-0"
                              onClick={() => setItemDlg(item)}
                              title="Edit item"
                            >
                              <Pencil size={14} />
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 rounded-lg p-0 text-red-600 hover:text-red-700"
                              onClick={() => requestDelete(item)}
                              title="Delete item"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {filteredItems.length > PAGE_SIZE && (
            <Pagination
              page={stockPage}
              totalPages={stockTotalPages}
              total={filteredItems.length}
              pageSize={PAGE_SIZE}
              onPageChange={setStockPage}
            />
          )}
        </>
      )}

      {/* ================= DIALOGS ================= */}

      <TxnDialog
        item={dlg}
        onClose={() => setDlg(null)}
        onDone={load}
        selectedDate={selectedDate === "all" ? undefined : selectedDate}
      />

      <InventoryItemDialog
        mode={itemDlg === "add" ? "add" : itemDlg ? "edit" : null}
        item={itemDlg !== "add" ? itemDlg : null}
        activeBranch={activeBranch}
        selectedDate={selectedDate === "all" ? undefined : selectedDate}
        onClose={() => setItemDlg(null)}
        onDone={load}
      />

      <ConfirmDeleteDialog
        item={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

/* =========================================================
   PAGINATION
========================================================= */

const Pagination = ({ page, totalPages, total, pageSize, onPageChange }) => {
  if (total <= pageSize) {
    return null;
  }

  const start = (page - 1) * pageSize + 1;

  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col gap-2 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-muted-foreground">
        Showing {start}-{end} of {total}
      </p>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 rounded-lg"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>

        <span className="min-w-20 text-center text-xs font-semibold">
          Page {page} of {totalPages}
        </span>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 rounded-lg"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

/* =========================================================
   TRANSACTION DIALOG
========================================================= */

const TxnDialog = ({ item, onClose, onDone, selectedDate }) => {
  const [type, setType] = useState("purchase");

  const [qty, setQty] = useState("");

  const [purchasePrice, setPurchasePrice] = useState("");

  const [supplier, setSupplier] = useState("");

  const [busy, setBusy] = useState(false);

  const [adjustmentDirection, setAdjustmentDirection] = useState("increase");

  const [transactionDate, setTransactionDate] = useState(
    selectedDate || getToday(),
  );

  useEffect(() => {
    if (!item) return;

    setType("purchase");
    setQty("");

    /*
     * Existing item ka current purchase
     * price default dikha sakte hain.
     */
    setPurchasePrice(item.purchase_price ?? "");
    setSupplier(item.supplier ?? "");
    setAdjustmentDirection("increase");

    setTransactionDate(selectedDate || getToday());
  }, [item, selectedDate]);

  const save = async () => {
    if (!item) return;

    const current = Number(item.current_stock) || 0;

    const quantity = Number(qty) || 0;

    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Enter a valid quantity");
      return;
    }

    const decreasesStock =
      type === "consumption" ||
      type === "wastage" ||
      type === "return" ||
      (type === "adjustment" && adjustmentDirection === "decrease");

    if (decreasesStock && quantity > current) {
      toast.error(`Stock cannot go below 0 ${item.unit}`);

      return;
    }

    let price;

    if (type === "purchase") {
      price = Number(purchasePrice);

      if (!Number.isFinite(price) || price < 0) {
        toast.error("Enter a valid purchase price");

        return;
      }
    }

    if (!transactionDate) {
      toast.error("Transaction date is required");

      return;
    }

    setBusy(true);

    try {
      const payload = {
        type,

        quantity,

        adjustment_direction:
          type === "adjustment" ? adjustmentDirection : null,

        transaction_date: transactionDate,
      };

      /*
       * Purchase ke time price backend
       * ko bhejna hai.
       */
      if (type === "purchase") {
        payload.purchase_price = price;

        payload.supplier = supplier.trim();
      }

      await api.inventoryTxn(item.id, payload);

      toast.success("Stock updated");

      await onDone();

      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update stock");
    } finally {
      setBusy(false);
    }
  };

  const previewStock = () => {
    const current = Number(item?.current_stock) || 0;

    const quantity = Number(qty) || 0;

    if (!quantity) {
      return current;
    }

    if (type === "purchase") {
      return current + quantity;
    }

    if (type === "return" || type === "consumption" || type === "wastage") {
      return current - quantity;
    }

    if (type === "adjustment" && adjustmentDirection === "increase") {
      return current + quantity;
    }

    return current - quantity;
  };

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="bg-white sm:max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="inv-txn-dialog"
      >
        {item && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                Update {item.name}
              </DialogTitle>
            </DialogHeader>

            <p className="text-sm text-muted-foreground">
              Current stock:{" "}
              <b className="text-foreground">
                {formatNumber(item.current_stock)} {item.unit}
              </b>
            </p>

            <div className="grid grid-cols-3 gap-2">
              {TXN.map((transactionType) => (
                <button
                  key={transactionType}
                  type="button"
                  onClick={() => setType(transactionType)}
                  className={cn(
                    "rounded-xl py-2 text-xs font-semibold capitalize border transition-colors",
                    type === transactionType
                      ? "bg-primary text-white border-primary"
                      : "bg-secondary/60 border-transparent hover:bg-secondary",
                  )}
                >
                  {transactionType}
                </button>
              ))}
            </div>

            {type === "adjustment" && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAdjustmentDirection("increase")}
                  className={cn(
                    "rounded-xl py-2 text-xs font-semibold border",
                    adjustmentDirection === "increase"
                      ? "bg-primary text-white border-primary"
                      : "bg-secondary/60 border-transparent",
                  )}
                >
                  Increase Stock
                </button>

                <button
                  type="button"
                  onClick={() => setAdjustmentDirection("decrease")}
                  className={cn(
                    "rounded-xl py-2 text-xs font-semibold border",
                    adjustmentDirection === "decrease"
                      ? "bg-primary text-white border-primary"
                      : "bg-secondary/60 border-transparent",
                  )}
                >
                  Decrease Stock
                </button>
              </div>
            )}

            <div className="rounded-xl bg-secondary/50 px-4 py-3">
              <p className="text-xs text-muted-foreground">Current Stock</p>

              <p className="text-lg font-bold">
                {formatNumber(item.current_stock)} {item.unit}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">
                Transaction Date
              </label>

              <Input
                type="date"
                value={transactionDate}
                max={getToday()}
                onChange={(e) => setTransactionDate(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="inv-txn-date"
              />
            </div>

            <Input
              type="number"
              min="0"
              step="0.001"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder={
                type === "purchase"
                  ? `Purchase quantity in ${item.unit}`
                  : `Quantity in ${item.unit}`
              }
              className="h-11 rounded-xl"
              data-testid="inv-txn-qty"
            />

            {type === "purchase" && (
              <>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  placeholder="Purchase price per unit"
                  className="h-11 rounded-xl"
                  data-testid="inv-txn-purchase-price"
                />
                <Input
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  placeholder="Supplier / Vendor"
                  className="h-11 rounded-xl"
                  data-testid="inv-txn-supplier"
                />
              </>
            )}

            {qty && Number(qty) > 0 && (
              <div className="rounded-xl bg-emerald-50 px-4 py-3">
                <p className="text-xs text-muted-foreground">New Stock</p>

                <p className="text-lg font-bold text-emerald-700">
                  {formatNumber(previewStock())} {item.unit}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                onClick={save}
                disabled={busy}
                className="w-full h-11 rounded-xl font-semibold"
                data-testid="inv-txn-save"
              >
                {busy ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* =========================================================
   ADD / EDIT INVENTORY ITEM
========================================================= */

const InventoryItemDialog = ({
  mode,
  item,
  activeBranch,
  selectedDate,
  onClose,
  onDone,
}) => {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    name: "",
    unit: "",
    opening_stock: "",
    min_stock: "",
    purchase_price: "",
    supplier: "",
    inventory_date: getToday(),
  });

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item && isEdit) {
      setForm({
        name: item.name || "",
        unit: item.unit || "",
        opening_stock: "",
        min_stock: item.min_stock ?? "",
        purchase_price: item.purchase_price ?? "",
        supplier: item.supplier || "",
        inventory_date: selectedDate || getToday(),
      });

      return;
    }

    setForm({
      name: "",
      unit: "",
      opening_stock: "",
      min_stock: "",
      purchase_price: "",
      supplier: "",
      inventory_date: selectedDate || getToday(),
    });
  }, [item, isEdit, selectedDate]);

  const change = (key, value) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const save = async () => {
    const name = form.name.trim();

    const unit = form.unit.trim();

    if (!name) {
      toast.error("Item name is required");
      return;
    }

    if (!unit) {
      toast.error("Unit is required");
      return;
    }

    if (!isEdit && !form.inventory_date) {
      toast.error("Inventory date is required");
      return;
    }

    const openingStock = Number(form.opening_stock);

    const minStock = Number(form.min_stock);

    const purchasePrice = Number(form.purchase_price);

    if (!isEdit && (!Number.isFinite(openingStock) || openingStock < 0)) {
      toast.error("Opening stock cannot be negative");
      return;
    }

    if (!Number.isFinite(minStock) || minStock < 0) {
      toast.error("Reorder level cannot be negative");
      return;
    }

    if (!Number.isFinite(purchasePrice) || purchasePrice < 0) {
      toast.error("Purchase price cannot be negative");
      return;
    }

    setBusy(true);

    try {
      const body = {
        name,
        unit,

        opening_stock: isEdit ? undefined : openingStock || 0,

        min_stock: minStock || 0,

        purchase_price: purchasePrice || 0,

        supplier: form.supplier.trim(),
      };

      if (!isEdit) {
        body.inventory_date = form.inventory_date;

        body.branch_id = activeBranch;
      }

      if (isEdit) {
        await api.updateInventory(item.id, body);

        toast.success("Inventory item updated");
      } else {
        await api.createInventory(body);

        toast.success("Inventory item added");
      }

      await onDone();

      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          `Failed to ${isEdit ? "update" : "add"} item`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!mode} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Edit Inventory Item" : "Add Inventory Item"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {isEdit && (
            <div className="rounded-xl bg-secondary/50 px-4 py-3 text-sm">
              <span className="text-muted-foreground">Current Stock</span>

              <span className="ml-2 font-bold text-foreground">
                {formatNumber(item.current_stock)} {item.unit}
              </span>
            </div>
          )}

          <Input
            value={form.name}
            onChange={(e) => change("name", e.target.value)}
            placeholder="Item name"
          />

          <select
            value={form.unit}
            onChange={(e) => change("unit", e.target.value)}
            className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
          >
            <option value="">Select unit...</option>

            <option value="kg">kg</option>

            <option value="litre">litre</option>

            <option value="pcs">pcs</option>

            <option value="gram">gram</option>

            <option value="ml">ml</option>

            <option value="dozen">dozen</option>
          </select>

          {!isEdit && (
            <Input
              type="number"
              min="0"
              step="0.001"
              value={form.opening_stock}
              onChange={(e) => change("opening_stock", e.target.value)}
              placeholder="Opening stock"
              className="h-11 rounded-xl"
            />
          )}

          <Input
            type="number"
            min="0"
            step="0.001"
            value={form.min_stock}
            onChange={(e) => change("min_stock", e.target.value)}
            placeholder="Reorder Level"
          />

          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.purchase_price}
            onChange={(e) => change("purchase_price", e.target.value)}
            placeholder="Purchase price per unit"
          />

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">
              Inventory Date
            </label>

            <Input
              type="date"
              value={form.inventory_date}
              max={getToday()}
              onChange={(e) => change("inventory_date", e.target.value)}
              className="h-11 rounded-xl"
              data-testid="inv-date"
            />

            <p className="text-[11px] text-muted-foreground">
              Defaults to today. Use this date for opening stock entry.
            </p>
          </div>

          <Input
            value={form.supplier}
            onChange={(e) => change("supplier", e.target.value)}
            placeholder="Supplier"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>

          <Button
            onClick={save}
            disabled={busy}
            className="rounded-xl font-semibold"
          >
            {busy ? "Saving..." : isEdit ? "Save Changes" : "Add Item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* =========================================================
   DELETE CONFIRMATION
========================================================= */

const ConfirmDeleteDialog = ({ item, onCancel, onConfirm }) => {
  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="bg-white sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display">
            Delete Inventory Item?
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-xl bg-red-50 px-4 py-3">
          <AlertCircle className="mt-0.5 shrink-0 text-red-600" size={20} />

          <p className="text-sm text-red-700">
            Are you sure you want to delete <b>{item?.name}</b>? This action
            cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-xl">
            Cancel
          </Button>

          <Button
            onClick={onConfirm}
            className="rounded-xl bg-red-600 font-semibold text-white hover:bg-red-700"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
